import { translate, Translation } from '@teamleader/i18n';
import { AnimationItem } from 'lottie-web';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { Form, Field } from 'react-final-form';
import { mutate } from 'swr';

import {
  useIsExtraLarge,
  useIsHuge,
  useIsSmall,
  useIsTouchscreen,
} from '../../../../../Components/Context/Hooks/mediaQueries';
import useLogoPosition from '../../../../../Components/Context/Hooks/useLogoPosition';
import AccessibilityText from '../../../../../Components/UI/AccessibilityText/AccessibilityText';
import Box from '../../../../../Components/UI/Box';
import Branding from '../../../../../Components/UI/Branding';
import Button from '../../../../../Components/UI/Button';
import FormItem from '../../../../../Components/UI/FormItem/FormItem';
import FormLabel from '../../../../../Components/UI/FormLabel/FormLabel';
import Input from '../../../../../Components/UI/Input';
import Link from '../../../../../Components/UI/Link';
import Lottie from '../../../../../Components/UI/Lottie';
import MobilePopup from '../../../../../Components/UI/MobilePopup';
import OverflowingFlexBox from '../../../../../Components/UI/OverflowingFlexBox';
import Sidebar, { SIZES } from '../../../../../Components/UI/Sidebar';
import SignatureInput, { METHODS, Signature } from '../../../../../Components/UI/SignatureInput';
import { WithTooltip } from '../../../../../Components/UI/Tooltip';
import Document from '../../../../../Svg/120x120_document.svg';
import signing from '../../../../../Svg/Lottie/signing.json';
import { DealsInfo } from '../../../../../Types/api';
import { CUSTOMER_PORTAL_STATUS } from '../../../../../Types/quotation';
import { EVENTS, track } from '../../../../../Utils/analytics';
import { canOpenNavigationForDeal } from '../../../../../Utils/canOpenNavigation';
import { MODES, UrlContext } from '../../../../../Utils/jwt';
import { captureException } from '../../../../../Utils/sentry';
import useGlobalContext, { DealContext } from '../../../Context';
import ErrorMessage from '../../ErrorMessage';
import SuccessMessage from '../../SuccessMessage';
import * as styles from '../../styles.css';

interface FormData {
  name?: string;
  signature?: Signature;
  email?: string;
}

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

enum SIGNING_STATE {
  IDLE = 'idle',
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILURE = 'failure',
}

const ButtonWithTooltip = WithTooltip(Button);

const SignMenu: React.FC = () => {
  const [signingState, setSigningState] = useState(SIGNING_STATE.IDLE);
  const { quotation, deal } = useContext(DealContext);
  const { handleCloseMenu, request, type } = useGlobalContext();
  const { mode } = useContext(UrlContext);
  const { inSuccessMessage } = useLogoPosition(canOpenNavigationForDeal(deal));
  const changedName = useRef(false);
  const isSmall = useIsSmall();
  const isTouchscreen = useIsTouchscreen();
  const isExtraLarge = useIsExtraLarge();
  const isHuge = useIsHuge();
  const Element = isSmall ? MobilePopup : Sidebar;
  const confirmationEmailAddress = deal!.signingEmailAddress || deal!.contactPerson?.email || deal!.client.email;
  const requestBody = useRef('');
  const signMethod = useRef<METHODS>();
  const animation = useRef<AnimationItem>(null);

  const submit = useCallback(() => {
    (async () => {
      setSigningState(SIGNING_STATE.PROCESSING);
      try {
        await request!('/deals.sign', {
          body: requestBody.current,
        });
      } catch (error) {
        setSigningState(SIGNING_STATE.FAILURE);
        captureException(error);
        return;
      }

      await new Promise<void>((resolve) => {
        if (!animation.current) {
          resolve();
        }

        animation.current!.addEventListener('loopComplete', () => resolve());
      });
      animation.current?.removeEventListener('loopComplete');

      setSigningState(SIGNING_STATE.SUCCESS);
      track(EVENTS.SIGNED, {
        style: signMethod.current,
        quotation: quotation!.id,
      });
      await mutate('/deals.info');
    })();
  }, [quotation, request]);

  const handleSubmit = useCallback(
    ({ name, signature, email }: FormData) => {
      requestBody.current = JSON.stringify({
        id: quotation!.id,
        name,
        signature: signature!.signature,
        confirmation_email_address: email || confirmationEmailAddress,
      });
      signMethod.current = signature!.method;

      submit();
    },
    [confirmationEmailAddress, quotation, submit],
  );

  const handleValidate = useCallback(
    ({ name, signature, email }: FormData) => {
      const errors: Record<string, boolean> = {};

      if (!name || name.trim().length === 0) {
        errors.name = true;
      }

      if (!signature || signature.signature.length === 0) {
        errors.signature = true;
      }

      if (!confirmationEmailAddress && (!email || !EMAIL_REGEX.test(email))) {
        errors.email = true;
      }

      return errors;
    },
    [confirmationEmailAddress],
  );

  const handleClickDisclaimer = useCallback(() => {
    track(EVENTS.CLICKED_TERMS_OF_SERVICE);
  }, []);

  const handleChangedName = useCallback((onChange: (...args: unknown[]) => void, ...args: unknown[]) => {
    onChange(...args);

    if (!changedName.current) {
      changedName.current = true;
      track(EVENTS.STARTED_ADDING_NAME);
    }
  }, []);

  const handleKeyUp = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      (event.target as HTMLInputElement).blur();
      event.preventDefault();
    }
  }, []);

  const handleRetry = useCallback(async () => {
    const { data } = (await mutate('/deals.info')) as DealsInfo;
    const updatedQuotation = data.quotations.find((updatedQuotation) => updatedQuotation.id === quotation!.id);

    if (!updatedQuotation || !requestBody.current) {
      location.reload();
      return;
    }

    if (updatedQuotation.customerPortalStatus === CUSTOMER_PORTAL_STATUS.SIGNED) {
      setSigningState(SIGNING_STATE.SUCCESS);
      return;
    }

    submit();
  }, [quotation, submit]);

  const handleCloseSignMenu = useCallback(() => {
    if (signingState === SIGNING_STATE.IDLE) {
      track(EVENTS.CANCELED_SIGNING);
    }

    handleCloseMenu!();
  }, [handleCloseMenu, signingState]);

  return (
    <Form
      onSubmit={handleSubmit}
      validate={handleValidate}
      render={({ handleSubmit, invalid }) => (
        <Element
          className={styles['menu']}
          display="flex"
          flexDirection="column"
          element="form"
          autoComplete="off"
          onSubmit={handleSubmit}
          onClose={signingState !== SIGNING_STATE.PROCESSING ? handleCloseSignMenu : undefined}
          size={isHuge ? SIZES.WIDEST : isExtraLarge ? SIZES.WIDER : undefined}
        >
          {signingState === SIGNING_STATE.SUCCESS && (
            <SuccessMessage
              icon={<Document />}
              title={translate('signSidebar.signedMessageTitle')}
              description={translate('signSidebar.signedMessageText', { name: deal!.department.name })}
              branding={
                inSuccessMessage ? (
                  <Branding language={deal?.client.language} country={deal?.client.country} type={type} />
                ) : undefined
              }
            />
          )}
          {signingState === SIGNING_STATE.FAILURE && (
            <ErrorMessage
              icon={<Document />}
              title={translate('signSidebar.errorMessageTitle')}
              description={translate('signSidebar.errorMessageText')}
              onRetry={handleRetry}
            />
          )}
          <Box
            flex={1}
            flexDirection="column"
            alignItems="center"
            className={styles['message']}
            paddingHorizontal={4}
            display={signingState === SIGNING_STATE.PROCESSING ? 'flex' : 'none'}
          >
            <Lottie
              ref={animation}
              animationData={signing}
              loop
              play={signingState === SIGNING_STATE.PROCESSING}
              className={styles['message-icon']}
            >
              <Document />
            </Lottie>
            <h2 className={styles['message-title']}>{translate('signSidebar.signingMessageTitle')}</h2>
            <p className={styles['message-description']}>{translate('signSidebar.signingMessageText')}</p>
          </Box>
          {signingState === SIGNING_STATE.IDLE && (
            <>
              <OverflowingFlexBox>
                <Box paddingHorizontal={4}>
                  <h3 className={styles['title']}>{translate('signSidebar.title', { title: quotation!.name })}</h3>
                  <Box marginVertical={4}>
                    <FormItem label={<FormLabel htmlFor="name">{translate('signSidebar.name')}</FormLabel>}>
                      <Field
                        name="name"
                        render={({ input, meta }): React.ReactNode => (
                          <Input
                            {...input}
                            id="name"
                            hasError={meta.touched && meta.error}
                            onChange={handleChangedName.bind(null, input.onChange)}
                            onKeyUp={handleKeyUp}
                            autoComplete="name"
                          />
                        )}
                      />
                    </FormItem>
                    {!confirmationEmailAddress && (
                      <FormItem label={<FormLabel htmlFor="email">{translate('signSidebar.email')}</FormLabel>}>
                        <Field
                          name="email"
                          render={({ input, meta }): React.ReactNode => (
                            <Input {...input} id="email" hasError={meta.touched && meta.error} onKeyUp={handleKeyUp} />
                          )}
                        />
                      </FormItem>
                    )}
                    <Field
                      name="signature"
                      render={({ input }): React.ReactNode => (
                        <SignatureInput
                          onChange={input.onChange}
                          height={isTouchscreen ? 240 : 120}
                          preferredMethod={isTouchscreen ? METHODS.DRAW : undefined}
                        />
                      )}
                    />
                    <Box element="p" className={styles['disclaimer']}>
                      <Translation
                        id="signSidebar.disclaimer"
                        values={{
                          link: (chunks: React.ReactNode[]): React.ReactNode => (
                            <Link
                              onClick={handleClickDisclaimer}
                              href={`/terms/${deal?.client.language || navigator.language}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {chunks}
                              <AccessibilityText>{translate('a11y.opensInNewTab')}</AccessibilityText>
                            </Link>
                          ),
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </OverflowingFlexBox>
              <Element.Footer padding={4}>
                <Box display="flex" justifyContent="flex-end">
                  <Button level="secondary" type="button" marginRight={3} onClick={handleCloseSignMenu}>
                    {translate('actions.cancel')}
                  </Button>
                  <ButtonWithTooltip
                    level="primary"
                    disabled={invalid}
                    label={!invalid && mode === MODES.PREVIEW ? translate('preview.actions') : undefined}
                    placement="left"
                  >
                    {translate('actions.sign')}
                  </ButtonWithTooltip>
                </Box>
                {deal!.quotations.length > 1 && (
                  <p className={styles['warning']}>{translate('signSidebar.singleAcceptedQuotationWarning')}</p>
                )}
              </Element.Footer>
            </>
          )}
        </Element>
      )}
    />
  );
};

export default SignMenu;
